import { useContext, useEffect, useState } from "react";
import MyBarChart from "../BarChart/barChart";
import DonutChart from "../DonutChart/DonutChart";

import { colors } from "../../Colors/colors";
import "./OEECard.less";
import UserContext from "../../themeContext/UserContext";
import { MachinesByGroup } from "../../Types/dashboardTypes";
import MachineDataContext from "../../themeContext/MachineDataContext";
import { HistoricalTsiDataType } from "../../Types/portalTypes";
import { getSubtractedDateStringValue } from "../../Common/commonFunctions";

interface OEECardProps {
  machinesByGroup: MachinesByGroup[];
  groupHistoricalData: HistoricalTsiDataType;
}
interface AvgOee {
  day: null | number;
  week: null | number;
}

const height = 275;
const width = 275;
const outerRadius = 0.9;
const innerRadius = 0.6;

const OEECard: React.FC<OEECardProps> = (props) => {
  const { machinesByGroup, groupHistoricalData } = props;
  const [metrics, setMetrics] = useState({ oee: 0, availability: 0, performance: 0, quality: 0 });
  const [avgOee, setAvgOee] = useState<AvgOee>({ day: null, week: null });
  const { userInfo } = useContext(UserContext);
  const currentMachineData = useContext(MachineDataContext);
  const accountType = userInfo.accountType;

  // CURRENT data averages are done here
  useEffect(() => {
    let oee = 0;
    let oeeCount = 0;
    let availability = 0;
    let availabilityCount = 0;
    let performance = 0;
    let performanceCount = 0;
    let quality = 0;
    let qualityCount = 0;

    machinesByGroup.map((value) => {
      if (currentMachineData[value.machineSerial] !== undefined) {
        if (currentMachineData[value.machineSerial].oee !== undefined) {
          oee += currentMachineData[value.machineSerial].oee;
          oeeCount += 1;
        }
        if (currentMachineData[value.machineSerial].availability !== undefined) {
          availability += currentMachineData[value.machineSerial].availability;
          availabilityCount += 1;
        }
        if (currentMachineData[value.machineSerial].performance !== undefined) {
          performance += currentMachineData[value.machineSerial].performance;
          performanceCount += 1;
        }
        if (currentMachineData[value.machineSerial].quality !== undefined) {
          quality += currentMachineData[value.machineSerial].quality;
          qualityCount += 1;
        }
      }
    });
    const avgOee = (oee / oeeCount) * 100;
    const avgAvailability = (availability / availabilityCount) * 100;
    const avgPerformance = (performance / performanceCount) * 100;
    const avgQuality = (quality / qualityCount) * 100;
    setMetrics({
      oee: avgOee ? Math.round(avgOee) : 0,
      availability: avgAvailability ? Math.round(avgAvailability) : 0,
      performance: avgPerformance ? Math.round(avgPerformance) : 0,
      quality: avgQuality ? Math.round(avgQuality) : 0,
    });
  }, [machinesByGroup, currentMachineData]);

  // HISTORICAL data averge oee (day and week) is calculated here
  useEffect(() => {
    if (Object.keys(groupHistoricalData).length > 0 && accountType != "free") {
      let todayCount = 0;
      let todayOeeSum = 0;
      let weekCount = 0;
      let weekOeeSum = 0;
      const today = getSubtractedDateStringValue(0);
      const oneWeekAgo = getSubtractedDateStringValue(7);
      Object.keys(groupHistoricalData).forEach((key) => {
        // do a today average, then a weekly average
        groupHistoricalData[key].map((value) => {
          if (value.date == today && value.oee) {
            todayCount += 1;
            todayOeeSum += value.oee;
          }
          if (value.date > oneWeekAgo && value.oee) {
            weekCount += 1;
            weekOeeSum += value.oee;
          }
        });
      });
      setAvgOee({
        day: Math.round((todayOeeSum / todayCount) * 100),
        week: Math.round((weekOeeSum / weekCount) * 100),
      });
    } else {
      setAvgOee({
        day: null,
        week: null,
      });
    }
  }, [groupHistoricalData, accountType]);

  return (
    <div className={`graph-oee-card`}>
      <div className="graph-oee-card-header">
        <div className="group-card-title">Group OEE</div>
        {accountType != "free" ? (
          <div className="graph-oee-card-footer">
            <div className="details">
              <span>Average today</span>
              <b>{avgOee.day + "%"}</b>
            </div>
            <div style={{ borderLeft: "1px solid", color: "var(--gray-3)", height: "40px" }} />
            <div className="details">
              <span>Avg. last 7 days</span>
              <b>{avgOee.week + "%"}</b>
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
      <div className="graph-oee-card-content">
        <div style={{ textAlign: "center" }}>
          <DonutChart
            data={[
              {
                label: "",
                value: metrics.oee,
              },
              {
                label: "",
                value: metrics.oee > 100 ? 0 : 100 - metrics.oee,
                isEmpty: true,
              },
            ]}
            legend={false}
            height={height}
            width={width}
            strokeColor={"#69C0FF"}
            colors={["#69C0FF"]}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            formatValues={() => `${metrics.oee.toFixed(0)}%`}
            className="oee-card-donut-chart"
          />
        </div>
        {accountType == "premium" ? (
          <MyBarChart
            colorTheme={colors.style2}
            metricProps={{
              quality: metrics.quality,
              availability: metrics.availability,
              performance: metrics.performance,
            }}
          />
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default OEECard;
